import { useState, useEffect } from "react";

import { ApplePaySupportedMerchantCapabilities, ApplePaySupportedNetworks, CountryCodes, DigitalWalletProviders, PaymentMethodType, TransactionStatus } from "../../common/constans";
import ApiClient from "../../ApiClient";

const apiClient = ApiClient();

const usePaymentLink = (
    sessionId, profileName, orderRef, merchantId) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingPaymentDetails, setIsFetchingPaymentDetails] = useState(true);
    const [isFetchingSessionInfo, setIsFetchingSessionInfo] = useState(true);
    const [isFetchingToken, setIsFetchingToken] = useState(true);
    const [isFetchingMerchantLogo, setIsFetchingMerchantLogo] = useState(true);
    const [isPaying, setIsPaying] = useState(false);
    const [paymentResponseDto, setPaymentResponseDto] = useState({});
    const [sessionInfo, setSessionInfo] = useState(null);
    const [paymentLinkDetails, setPaymentLinkDetails] = useState(null);
    const [merchantLogo, setMerchantLogo] = useState(null);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);

    const { 
        pay,
        getTransactionSessionInfo,
        applePayMerchantValidation,
        payV2,
        getLiveLinkDetails,
        getMerchantLogo,
        initLiveLinkPayment } = apiClient;

    useEffect(() => {
        if (!profileName || !orderRef) return;

        const getTokenFromFullUrl = (url) => {
            const parsedUrl = new URL(url);
            return parsedUrl.searchParams.get("token");
        };

        const onSuccess = (response) => {
            console.log("token: ", response);
            if (response.succeeded === true) {
                setToken(getTokenFromFullUrl(response.response.checkout_url));
            } else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsFetchingToken(false);
            setIsFetchingSessionInfo(false);
        }

        const fetch = async () => {
            try {
                setIsFetchingToken(true);
                await initLiveLinkPayment(profileName, {}, orderRef, onSuccess, onFail);
            } catch (e) {
                setError(e);
            } finally {
                setIsFetchingToken(false);
            }
        }

        fetch();
    }, [profileName, orderRef, initLiveLinkPayment]);

    useEffect(() => {
        if (!token) return;

        const onSuccess = (response) => {
            if (response.succeeded === true) {
                setSessionInfo(response);
            }
            else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsFetchingSessionInfo(false);
        }

        const fetch = async () => {
            try {
                setIsFetchingSessionInfo(true);
                await getTransactionSessionInfo(token, sessionId, onSuccess, onFail);
            } catch (e) {
                setError(e);
            } finally {
                setIsFetchingSessionInfo(false);
            }
        }

        fetch();

    }, [token, sessionId, getTransactionSessionInfo]);

    useEffect(() => {
        if (!orderRef) return;

        const onGetLiveLinkDetailsSuccess = (response) => {
            if (response.succeeded === true) {
                setPaymentLinkDetails(response.response);
            }
            else {
                setError(response.error_message);
            }
        }

        const onFail = () => {
            setError("Couldn't fetch payment details");
        }

        const fetchDetails = async () => {
            try {
                setIsFetchingPaymentDetails(true); 
                await getLiveLinkDetails(profileName, orderRef, onGetLiveLinkDetailsSuccess, onFail);
            } catch (err) {
                setError(err);
            } finally {
                setIsFetchingPaymentDetails(false); 
            }
        };

        fetchDetails();
    }, [orderRef, profileName, getLiveLinkDetails]);

    useEffect(() => {
        if (!merchantId) return;

        const onGetMerchantLogoSuccess = (response) => {
            setMerchantLogo(response.response.merchant_logo);
        }

        const fetchLogo = async () => {
            try {
                setIsFetchingMerchantLogo(true);
                await getMerchantLogo(merchantId, onGetMerchantLogoSuccess, () => {});
            } catch (err) {
                console.log(err);
            } finally {
                setIsFetchingMerchantLogo(false);
            }
        }

        fetchLogo();
    }, [merchantId, getMerchantLogo]);

    useEffect(() => {
        setIsLoading(isFetchingPaymentDetails | isFetchingSessionInfo | isFetchingToken | isFetchingMerchantLogo);
    }, [isFetchingToken, isFetchingPaymentDetails, isFetchingSessionInfo, isFetchingMerchantLogo]);

    const handleApplePayPayment = () => {
        
        setIsPaying(true);
        const paymentRequest = {
            countryCode: CountryCodes.EGYPT,
            currencyCode: sessionInfo.currency,
            supportedNetworks: ApplePaySupportedNetworks,
            merchantCapabilities: ApplePaySupportedMerchantCapabilities,
            total: {
                label: sessionInfo.merchant_display_name,
                amount: sessionInfo.amount.toFixed(2)
            },
        };

        const session = new window.ApplePaySession(3, paymentRequest);

        const onMerchantValidationSuccess = (response) => {
            if (Object.hasOwn(response, "succeeded") && response.succeeded && response.session) {
                return session.completeMerchantValidation(response.session);
            }
            alert(response.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        const onMerchantValidationFailure = (error) => {
            alert(error.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        session.onvalidatemerchant = async (event) => {
            const requestDTO = {
                validation_url: event.validationURL,
                display_name: sessionInfo.merchant_display_name
            }
            await applePayMerchantValidation(requestDTO, token, onMerchantValidationSuccess, onMerchantValidationFailure);
        };
        const onSuccess = (responseDto) => {
            setPaymentResponseDto(responseDto);
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(`${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        const onPaymentChargeSuccess = (response) => {
            if (response.succeeded && !response.error_message) {
                session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                onSuccess(response)
            } else {
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                onFail({ message: response.error_message, ...response })
            }
        }

        const onPaymentChargeFail = (error) => {
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            console.log(error);
            alert(error.message);
        }

        session.onpaymentauthorized = async (event) => {
            try {
                const payload = {
                    session_id: sessionInfo.sessionId,
                    payment_token: JSON.stringify(event.payment.token.paymentData),
                    digital_wallet_provider: DigitalWalletProviders.ApplePay
                }
                await payV2(payload, token, onPaymentChargeSuccess, onPaymentChargeFail);
            } catch (err) {
                console.log("Error Payment", err);
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            }
        };

        session.oncancel = event => {
            setIsPaying(false);
        };

        session.begin();
    };

    const submitPayment = async (formData, selectedPaymentMethod) => {
        if (selectedPaymentMethod === PaymentMethodType.ApplePay) {
            return handleApplePayPayment()
        }
        
        const getExpirationMonth = () => {
            return +formData.expiry_date.substring(0, 2);
        }

        const getExpirationYear = () => {
            const year = Math.floor(new Date().getFullYear() / 100) * 100;
            return year + (+formData.expiry_date.substring(3, 5));
        }

        const getPaymentTransactionRequestDto = (formData) => {
            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(),
                "expiration_year": getExpirationYear(),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "security_code": formData.cvv,
                "should_save_payment_method": formData.should_save_payment_method,
                "session_id": sessionId,
            }
        }

        const onSuccess = (responseDto) => {
            setPaymentResponseDto(responseDto);
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(`${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        try {
            setIsPaying(true);
            await pay(getPaymentTransactionRequestDto(formData), token, onSuccess, onFail);
        } catch (err) {
            setError(err);
        } finally {
            setIsPaying(false);
        }
    };

    return {
        isLoading,
        sessionInfo,
        error,
        paymentLinkDetails,
        merchantLogo,
        paymentResponseDto,
        isPaying,
        submitPayment };
};

export default usePaymentLink;
